
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

// Define a union type of all RPC function names from the Database type
type RpcFunction = keyof Database["public"]["Functions"];

// Helper type to extract parameter type for a specific function
type RpcParams<T extends RpcFunction> = Database["public"]["Functions"][T]["Args"];

// Helper type to extract return type for a specific function
type RpcReturn<T extends RpcFunction> = Database["public"]["Functions"][T]["Returns"];

/**
 * A typed wrapper for Supabase RPC calls to avoid TypeScript errors
 */
export async function callRpcFunction<T extends RpcFunction>(
  functionName: T,
  params: RpcParams<T> = {} as RpcParams<T>
): Promise<{
  data: RpcReturn<T> | null;
  error: any | null;
}> {
  try {
    console.log(`Calling RPC function: ${functionName}`);
    // Use direct type assertion with the supabase client
    const { data, error } = await supabase.rpc(functionName, params as any);
    
    if (error) {
      console.error(`RPC function error (${functionName}):`, error);
    } else {
      console.log(`RPC function success (${functionName}):`, data);
    }
    
    return {
      data: data as RpcReturn<T>,
      error
    };
  } catch (err) {
    console.error(`Exception in RPC function (${functionName}):`, err);
    return {
      data: null,
      error: err
    };
  }
}

/**
 * Call RPC functions that are not yet in the generated types
 * This is a fallback for functions that haven't been added to the Database type yet
 */
export async function callUntypedRpcFunction<T>(
  functionName: string,
  params: Record<string, any> = {}
): Promise<{
  data: T | null;
  error: any | null;
}> {
  try {
    console.log(`Calling untyped RPC function: ${functionName}`);
    // Use type assertion to bypass TypeScript's type checking for the function name
    const { data, error } = await supabase.rpc(functionName as any, params);
    
    if (error) {
      console.error(`RPC function error (${functionName}):`, error);
    } else {
      console.log(`RPC function success (${functionName}):`, data);
    }
    
    return {
      data: data as T,
      error
    };
  } catch (err) {
    console.error(`Exception in RPC function (${functionName}):`, err);
    return {
      data: null,
      error: err
    };
  }
}
